import { useEffect, useRef, useState } from "react";
import { Target } from "lucide-react";

const avg = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : 0);

export const TimingBarGame = ({ difficulty, onDone }) => {
  const { rounds, sweet_spot, speed, maneuvers } = difficulty;
  const [round, setRound] = useState(0);
  const [pos, setPos] = useState(0);
  const [last, setLast] = useState(null);
  const posRef = useRef(0);
  const dirRef = useRef(1);
  const scoresRef = useRef([]);
  const raf = useRef();

  useEffect(() => {
    let t0 = performance.now();
    const tick = (t) => {
      const dt = Math.min(0.05, (t - t0) / 1000);
      t0 = t;
      let p = posRef.current + dirRef.current * speed * dt;
      if (p >= 1) { p = 1; dirRef.current = -1; }
      if (p <= 0) { p = 0; dirRef.current = 1; }
      posRef.current = p;
      setPos(p);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [speed]);

  const lock = () => {
    const dist = Math.abs(posRef.current - 0.5);
    const half = sweet_spot / 2;
    const s = dist <= half ? 1 - (dist / half) * 0.35 : Math.max(0, 0.55 - (dist - half) * 1.4);
    const next = [...scoresRef.current, s];
    scoresRef.current = next;
    setLast(s);
    if (next.length >= rounds) {
      cancelAnimationFrame(raf.current);
      setTimeout(() => onDone(avg(next)), 400);
      setRound(rounds);
    } else {
      setRound(next.length);
    }
  };

  const done = round >= rounds;
  const halfPct = (sweet_spot / 2) * 100;

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="minigame-handling">
      <div className="flex items-center justify-between text-xs font-bold text-primary">
        <span className="flex items-center gap-1"><Target size={14} /> {done ? "Done!" : maneuvers[round]}</span>
        <span data-testid="handling-progress">Maneuver {Math.min(round + 1, rounds)} / {rounds}</span>
      </div>

      <div className="relative mt-4 h-9 overflow-hidden rounded-full bg-muted">
        <div className="absolute top-0 h-full bg-green-400/60"
          style={{ left: `${50 - halfPct}%`, width: `${halfPct * 2}%` }} />
        <div className="absolute top-1/2 h-full w-1.5 -translate-y-1/2 rounded-full bg-primary shadow"
          style={{ left: `calc(${pos * 100}% - 3px)` }} data-testid="handling-marker" />
      </div>

      {last !== null && (
        <p className="mt-2 text-center text-xs font-bold" data-testid="handling-last"
          style={{ color: last >= 0.75 ? "#15803d" : last >= 0.4 ? "#b45309" : "#b91c1c" }}>
          {last >= 0.85 ? "Perfect!" : last >= 0.6 ? "Nice!" : last >= 0.35 ? "Okay" : "Missed the mark"}
        </p>
      )}

      <button data-testid="handling-lock-btn" disabled={done} onClick={lock}
        className="mt-3 h-14 w-full rounded-xl bg-primary text-lg font-bold text-primary-foreground disabled:opacity-40 active:scale-95">
        LOCK
      </button>
    </div>
  );
};
